import type { TFunction } from 'i18next';
import type { ColumnDef } from '@tanstack/react-table';

import { Button, Money, StatusPill } from '@/shared/ui';
import { formatNumber } from '@/shared/utils/number';

import { canAdminCancel, canAdminReturn } from '../lib/orderCancelReturn';
import { canContinuePendingPayment } from '../lib/pendingPaymentContinuation';
import { orderStatusIcon, orderStatusTone } from '../lib/orderStatusPresentation';
import type { OrderCancelReturnAction, OrderRow } from '../types';
import { ContinuePendingPaymentButton } from './ContinuePendingPaymentButton';

export interface CreateOrderColumnsParams {
  t: TFunction<'orders'>;
  onAction: (row: OrderRow, action: OrderCancelReturnAction) => void;
  onContinue: (row: OrderRow) => void;
  continuingOrderId?: string;
  /** Dashboard `admin` is the quick-commerce super-admin session. */
  canContinuePendingPayment: boolean;
}

export function createOrderColumns({
  t,
  onAction,
  onContinue,
  continuingOrderId,
  canContinuePendingPayment: allowContinue,
}: CreateOrderColumnsParams): ColumnDef<OrderRow, unknown>[] {
  return [
    {
      accessorKey: 'orderNumber',
      header: t('table.orderId'),
      cell: ({ row }) => <span className="font-semibold text-fg">{row.original.orderNumber}</span>,
    },
    {
      accessorKey: 'customerName',
      header: t('table.customer'),
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-fg">{row.original.customerName}</div>
          <div className="text-xs text-fg-muted">
            <Money amount={row.original.totalPaise} />
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: t('table.status'),
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <StatusPill tone={orderStatusTone[status]} icon={orderStatusIcon[status]}>
            {t(`status.${status}`)}
          </StatusPill>
        );
      },
    },
    {
      accessorKey: 'totalItems',
      header: t('table.totalItems'),
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.totalItems)}</span>
      ),
    },
    {
      accessorKey: 'originName',
      header: t('table.origin'),
      cell: ({ row }) => <span className="font-medium text-fg">{row.original.originName}</span>,
    },
    {
      id: 'actions',
      header: t('table.action'),
      cell: ({ row }) => {
        const canCancel = canAdminCancel(row.original.status);
        const canReturn = canAdminReturn(row.original);
        const showContinue =
          allowContinue && canContinuePendingPayment(row.original.status);
        const continuing = continuingOrderId === row.original.id;
        const anotherContinuing = Boolean(continuingOrderId) && !continuing;

        if (!showContinue && !canCancel && !canReturn) {
          return <span className="text-xs text-fg-muted">—</span>;
        }

        return (
          <div className="flex flex-wrap items-center gap-2">
            {showContinue ? (
              <ContinuePendingPaymentButton
                pending={continuing}
                disabled={anotherContinuing}
                t={t}
                onClick={() => onContinue(row.original)}
              />
            ) : null}
            {canCancel ? (
              <Button
                type="button"
                size="sm"
                variant="danger"
                onClick={(event) => {
                  event.stopPropagation();
                  onAction(row.original, 'CANCEL');
                }}
              >
                {t('table.cancel')}
              </Button>
            ) : null}
            {canReturn ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={(event) => {
                  event.stopPropagation();
                  onAction(row.original, 'RETURN');
                }}
              >
                {t('table.return')}
              </Button>
            ) : null}
          </div>
        );
      },
    },
  ];
}
